import React, { useState } from "react";
import moment from "moment";
import { ButtonGroup, Button, Grid } from "@material-ui/core";
import { styled } from "@material-ui/styles";
import { injectIntl } from "react-intl";
import messages from "./PageDashboard.intl";
import { IStyledArguments } from "../../types/styled-arguments";

const CalendarWrapper = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderRadius: 16,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    borderRadius: 14,
    padding: theme.spacing(2),
  },
}));

const CalendarTitle = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 600,
  color: theme.palette.text.primary,
  fontSize: "1.1rem",
  marginBottom: theme.spacing(2.5),
  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
    marginBottom: theme.spacing(2),
  },
}));

const StyledButtonGroup = styled(ButtonGroup)(({ theme }: IStyledArguments) => ({
  marginBottom: theme.spacing(1.5),
  borderRadius: 12,
  overflow: "hidden",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  "& .MuiButton-root": {
    border: "none",
    borderRight: `1px solid ${theme.palette.extra.borderColor}`,
    fontWeight: 500,
    fontSize: "0.9rem",
    padding: theme.spacing(1.5),
    borderRadius: 0,
    "&:last-child": {
      borderRight: "none",
    },
  },
  [theme.breakpoints.down("sm")]: {
    "& .MuiButton-root": {
      fontSize: "0.75rem",
      padding: theme.spacing(1),
    },
  },
}));

const DayButton = styled(Button)<{ $isToday?: boolean; $isTraining?: boolean; $isMatch?: boolean; $isPast?: boolean }>(
  ({ theme, $isToday, $isTraining, $isMatch, $isPast }: any) => ({
    background: $isMatch
      ? theme.palette.primary.main
      : $isTraining
      ? theme.palette.extra.warning
      : $isToday
      ? theme.palette.extra.accentSoft
      : theme.palette.background.paper,
    color: $isMatch || $isTraining
      ? `${theme.palette.primary.contrastText} !important`
      : $isPast
      ? "rgba(16, 21, 26, 0.3) !important"
      : `${theme.palette.text.primary} !important`,
    fontWeight: 600,
    fontSize: "1rem",
    "&:hover": {
      background: $isMatch
        ? theme.palette.primary.dark
        : $isTraining
        ? theme.palette.extra.warning
        : theme.palette.extra.accentSoft,
    },
    "&.Mui-disabled": {
      opacity: 0.45,
      color: "rgba(16, 21, 26, 0.26) !important",
      background: $isMatch
        ? theme.palette.primary.main
        : $isTraining
        ? theme.palette.extra.warning
        : theme.palette.background.paper,
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.85rem",
    },
  })
);

const WeekDayButton = styled(Button)<{ $isActive?: boolean }>(({ theme, $isActive }: any) => ({
  background: $isActive ? theme.palette.extra.accentSoft : theme.palette.background.paper,
  color: $isActive
    ? `${theme.palette.primary.dark} !important`
    : "rgba(16, 21, 26, 0.55) !important",
  fontWeight: $isActive ? 600 : 500,
  fontSize: "0.8rem",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  "&:hover": {
    background: theme.palette.extra.accentSoft,
  },
  "&.Mui-disabled": {
    opacity: 0.45,
    color: "rgba(16, 21, 26, 0.26) !important",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.7rem",
    padding: theme.spacing(0.75),
  },
}));

const LegendRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2.5),
  flexWrap: "wrap",
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

const LegendChip = styled("div")<{ $variant?: "training" | "match" | "today" }>(
  ({ theme, $variant }: any) => ({
    flex: 1,
    minWidth: "110px",
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 8,
    textAlign: "center",
    fontSize: "0.72rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    border: "1px solid",
    borderColor:
      $variant === "training"
        ? "rgba(245, 124, 0, 0.3)"
        : "rgba(14, 124, 134, 0.25)",
    background:
      $variant === "training"
        ? theme.palette.extra.warningSoft
        : $variant === "match"
        ? theme.palette.extra.accentSoft
        : theme.palette.extra.backgroundGrey,
    color:
      $variant === "training"
        ? theme.palette.extra.warning
        : $variant === "match"
        ? theme.palette.primary.dark
        : theme.palette.text.secondary,
    [theme.breakpoints.down("sm")]: {
      minWidth: "90px",
      padding: theme.spacing(0.75, 1.25),
      fontSize: "0.68rem",
    },
  })
);

interface ICalendarComponent {
  nextMatch: any;
  trainingWeekList: any;
}

const CalendarComponent = injectIntl<ICalendarComponent>(
  ({ nextMatch, trainingWeekList, intl }) => {
    const today = moment();
    const begin = moment(today).isoWeekday(1);
    const buildWeek = [];
    for (let i = 0; i < 7; i++) {
      buildWeek.push(
        moment(begin)
          .add(i, "days")
          .date()
      );
    }
    const [week] = useState(buildWeek);
    const currentDay = moment().isoWeekday();
    const currentDate = moment().date();

    const dayNames = [
      { key: 1, msg: messages.mondayShort },
      { key: 2, msg: messages.tuesdayShort },
      { key: 3, msg: messages.wednesdayShort },
      { key: 4, msg: messages.thursdayShort },
      { key: 5, msg: messages.fridayShort },
      { key: 6, msg: messages.saturdayShort },
      { key: 7, msg: messages.sundayShort },
    ];

    return (
      <CalendarWrapper>
        <CalendarTitle>Weekly Calendar</CalendarTitle>
        
        <Grid xs={12}>
          <StyledButtonGroup fullWidth variant="text">
            {dayNames.map((day) => (
              <WeekDayButton
                key={day.key}
                $isActive={day.key === currentDay}
                disabled={day.key < currentDay}
              >
                {intl.formatMessage(day.msg)}
              </WeekDayButton>
            ))}
          </StyledButtonGroup>
        </Grid>

        <Grid xs={12}>
          <StyledButtonGroup fullWidth>
            {week &&
              week.map((day: number) => {
                const isMatch = nextMatch && day === moment(nextMatch.date).date();
                const isTraining = trainingWeekList.includes(day);
                const isToday = day === currentDate;
                const isPast = day < currentDate;

                return (
                  <DayButton
                    key={day}
                    $isMatch={isMatch}
                    $isTraining={isTraining}
                    $isToday={isToday}
                    $isPast={isPast}
                    disabled={isPast}
                  >
                    {day}
                  </DayButton>
                );
              })}
          </StyledButtonGroup>
        </Grid>

        <LegendRow>
          <LegendChip $variant="training">
            {intl.formatMessage(messages.legendTrainings)}
          </LegendChip>
          <LegendChip $variant="match">
            {intl.formatMessage(messages.legendMatches)}
          </LegendChip>
          <LegendChip $variant="today">
            {intl.formatMessage(messages.legendToday)}
          </LegendChip>
        </LegendRow>
      </CalendarWrapper>
    );
  }
);

export default CalendarComponent;
